/**
 * NOTIFICATIONS. UF-08.
 *
 * This screen used to say "nothing to tell you yet" and mean it literally: the notification service
 * had no `main.py`, so there was no endpoint to ask. Rows were being written and served to nobody.
 *
 * ## What this screen is actually for
 *
 * One of these messages is worth more than all the others combined: the security alert that says
 * somebody signed in to your account.
 *
 * It is the ONE moment the real owner of a stolen account can notice a stranger in it. Everything
 * else here is a receipt. So the security alerts are visually distinct, they cannot be silenced
 * (FR-X-NOT-002), and the action they recommend is one tap away and actually works — sessions are
 * revocable, so "sign out everywhere" ends the attacker's session immediately (ADR 0006).
 *
 * ## Read is not acknowledged
 *
 * Marking everything read clears the bell. It acknowledges NOTHING. A mandatory notice stays
 * unacknowledged, and the flow that depends on it stays blocked, however many times somebody taps
 * "mark all read" — because scrolling past a risk disclosure must never count as accepting it, and a
 * consent record produced that way is not evidence of consent.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { fmtDateTime } from '@/lib/format';
import { Link } from 'react-router-dom';
import { api, LipaApiError, type Notification } from '@/lib/api';
import { EmptyState, ErrorState, LoadingCard } from '@/components/states';

const SECURITY = new Set(['security.login', 'security.device_change', 'risk.alert']);

/** A category icon + tint. The category prefix (before the dot) decides the family. */
function categoryVisual(category: string): { icon: ReactNode; tone: string } {
  const family = category.split('.')[0];
  const tone =
    family === 'security' || family === 'risk'
      ? 'bg-danger/10 text-danger'
      : family === 'transaction' || family === 'txn'
        ? 'bg-primary/10 text-brand'
        : family === 'wallet'
          ? 'bg-accent/12 text-accent'
          : family === 'tier' || family === 'identity'
            ? 'bg-info/15 text-info'
            : 'bg-surface-sunken text-ink-muted';

  const paths: Record<string, string> = {
    security: 'M12 3 4 6v5c0 4.5 3.2 8 8 10 4.8-2 8-5.5 8-10V6l-8-3Zm0 6v3m0 3h.01',
    risk: 'M12 3 4 6v5c0 4.5 3.2 8 8 10 4.8-2 8-5.5 8-10V6l-8-3Zm0 6v3m0 3h.01',
    transaction: 'M7.5 7.5h11m0 0-3-3m3 3-3 3M16.5 16.5h-11m0 0 3 3m-3-3 3-3',
    txn: 'M7.5 7.5h11m0 0-3-3m3 3-3 3M16.5 16.5h-11m0 0 3 3m-3-3 3-3',
    wallet:
      'M3 8.25A2.25 2.25 0 0 1 5.25 6h13.5A2.25 2.25 0 0 1 21 8.25v9A2.25 2.25 0 0 1 18.75 19.5H5.25A2.25 2.25 0 0 1 3 17.25v-9Zm0 3h18M16.5 14.25h1.5',
  };
  const d =
    paths[family] ??
    'M14.857 17.082a24 24 0 0 0 3.844-.815 1.5 1.5 0 0 0 .5-2.6A5 5 0 0 1 18 10V9A6 6 0 0 0 6 9v1a5 5 0 0 1-1.2 3.667 1.5 1.5 0 0 0 .5 2.6c1.255.337 2.54.61 3.845.815m6.512 0a3 3 0 0 1-6.512 0';
  return {
    tone,
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={d} />
      </svg>
    ),
  };
}

export default function Notifications() {
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  const load = useCallback(async () => {
    setError(null);
    try {
      setItems(await api.notifications());
    } catch (e) {
      setError(
        e instanceof LipaApiError ? e.detail.message : 'We could not load your notifications.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function readAll() {
    await api.markAllRead();
    await load();
  }

  if (loading) return <LoadingCard lines={4} />;
  if (error) return <ErrorState body={error} onRetry={() => void load()} />;

  const unread = items.filter((n) => !n.read).length;
  const needsAcknowledgement = items.filter((n) => n.mandatory && !n.acknowledged);
  const visible = filter === 'unread' ? items.filter((n) => !n.read) : items;

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-page-title text-ink">Notifications</h1>
        {unread > 0 ? (
          <button onClick={readAll} className="text-sm font-medium text-brand hover:underline">
            Mark all read
          </button>
        ) : null}
      </div>

      {/* Filter. Unread is the common triage view. */}
      <div
        role="tablist"
        aria-label="Filter"
        className="inline-flex gap-1 rounded-pill bg-canvas p-1"
      >
        {(['all', 'unread'] as const).map((f) => (
          <button
            key={f}
            role="tab"
            aria-selected={filter === f}
            onClick={() => setFilter(f)}
            className={`rounded-pill px-3.5 py-1 text-sm font-medium capitalize transition ${
              filter === f ? 'bg-surface text-ink shadow-sm' : 'text-ink-muted hover:text-ink'
            }`}
          >
            {f}
            {f === 'unread' && unread > 0 ? (
              <span className="ml-1 rounded-full bg-primary px-1.5 text-nano text-white">
                {unread}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {/* MANDATORY NOTICES THAT ARE STILL BLOCKING. FR-X-DSP-004: dependent flows do not continue
          until these are acknowledged, so they go at the top and they are not a list item. */}
      {needsAcknowledgement.length > 0 ? (
        <section className="space-y-3">
          {needsAcknowledgement.map((n) => (
            <div
              key={n.id}
              className="rounded-card border border-info/30 bg-info/5 p-4"
              role="alert"
            >
              <p className="text-sm font-medium text-ink">{n.subject}</p>
              <p className="mt-1 text-sm leading-relaxed text-ink-muted">{n.content}</p>

              <button
                onClick={async () => {
                  await api.acknowledge(n.id);
                  await load();
                }}
                className="mt-3 w-full rounded-lg bg-primary py-2 text-sm font-medium text-white transition hover:bg-primary-light"
              >
                I have read and understood this
              </button>

              <p className="mt-2 text-center text-xs text-ink-muted">
                This one cannot be dismissed. It has to be acknowledged.
              </p>
            </div>
          ))}
        </section>
      ) : null}

      {visible.length === 0 ? (
        <EmptyState
          title={filter === 'unread' ? 'You are all caught up' : 'Nothing to tell you yet'}
          body={
            filter === 'unread'
              ? 'No unread notifications.'
              : 'When money moves, your verification changes, or somebody signs in to your account, it will appear here.'
          }
        />
      ) : (
        <ul className="space-y-2">
          {visible.map((n) => {
            const security = SECURITY.has(n.category);
            const visual = categoryVisual(n.category);

            return (
              <li key={n.id}>
                <button
                  onClick={async () => {
                    if (!n.read) {
                      await api.markRead(n.id);
                      await load();
                    }
                  }}
                  className={`flex w-full gap-3 rounded-card border p-4 text-left transition ${
                    security
                      ? 'border-danger/30 bg-danger/5'
                      : n.read
                        ? 'border-border bg-surface hover:bg-surface-sunken'
                        : 'border-primary/30 bg-primary/5'
                  }`}
                >
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${security ? 'bg-danger/15 text-danger' : visual.tone}`}
                  >
                    {visual.icon}
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <p className={`text-sm font-medium ${security ? 'text-danger' : 'text-ink'}`}>
                        {n.subject}
                      </p>
                      {!n.read ? (
                        <span
                          aria-label="Unread"
                          className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary"
                        />
                      ) : null}
                    </div>
                    <p className="mt-1 text-sm leading-relaxed text-ink-muted">{n.content}</p>
                    <p className="mt-1.5 text-xs text-ink-subtle">{fmtDateTime(n.at)}</p>
                  </div>
                </button>

                {/* THE ACTION, one tap away, on the one message where it matters. An alert that told
                    somebody their account had been entered and then left them to find the setting
                    themselves would be an alert that arrived too late. */}
                {security ? (
                  <Link
                    to="/profile"
                    className="mt-1 block rounded-field bg-danger py-2 text-center text-sm font-medium text-white transition hover:opacity-90"
                  >
                    This was not me, sign out everywhere
                  </Link>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <p className="text-center text-xs text-ink-muted">
        Security, regulatory and risk notices are always delivered and cannot be turned off.
      </p>

      {/*
        HONEST ABOUT WHAT IS NOT REAL: the email and SMS channels are SIMULATORS. Africa's Talking is
        the intended provider for Kenya (ADR 0003) and is a commercial contract. A person's phone does
        not buzz today. The in-app copy — this screen — is the durable record, and it is real.
      */}
    </div>
  );
}
